import { useState } from "react";
import { Check, Loader2, ExternalLink, RefreshCw, Crown, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { useSubscription, useCreateCheckout, useCustomerPortal, useRefreshSubscription } from "@/hooks/useSubscription";
import { usePlan } from "@/hooks/usePlan";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";

const PRO_FEATURES = [
  "Projetos ilimitados",
  "Timer avançado com categorias",
  "Relatórios completos",
  "Exportação PDF/CSV",
  "Armazenamento ilimitado",
  "Suporte prioritário",
];

export function PlanSection() {
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription();
  const { isPro: planIsPro, isTrial, isTrialExpired, trialDaysUsed, trialDaysRemaining } = usePlan();
  const createCheckout = useCreateCheckout();
  const customerPortal = useCustomerPortal();
  const refreshSubscription = useRefreshSubscription();
  const { toast } = useToast();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const isPro = planIsPro || subscription?.subscribed;

  const handleSubscribe = async (plan: 'mensal' | 'anual') => {
    setLoadingPlan(plan);
    try {
      const { url } = await createCheckout.mutateAsync(plan);
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível iniciar o checkout. Tente novamente.",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { url } = await customerPortal.mutateAsync();
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error("Portal error:", error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível abrir o portal. Tente novamente.",
      });
    }
  };

  const handleRefresh = async () => {
    try {
      await refreshSubscription.mutateAsync();
      toast({
        title: "Atualizado!",
        description: "Status da assinatura atualizado.",
      });
    } catch (error) {
      console.error("Refresh error:", error);
    }
  };

  if (subscriptionLoading) {
    return (
      <div className="h-32 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return;
    
    setRedeeming(true);
    try {
      const { data, error } = await supabase.rpc('redeem_promo_code', {
        promo_code: promoCode.trim()
      });
      
      if (error) throw error;
      
      if (data && data.success) {
        toast({ title: "Sucesso!", description: data.message });
        setPromoCode("");
        // Atualiza a subscription localmente
        await refreshSubscription.mutateAsync();
      } else {
        toast({ variant: "destructive", title: "Erro", description: data?.error || "Código inválido." });
      }
    } catch (error: any) {
      toast({ variant: "destructive", title: "Erro", description: "Ocorreu um erro ao validar o código." });
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Trial Status Banner */}
      {isTrial && !isPro && (
        <div className="rounded-lg border-2 border-amber-500/50 bg-amber-500/10 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-500/20">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold">Período de Teste</h4>
                <Badge variant="outline" className="border-amber-500/50 text-amber-600">
                  {trialDaysRemaining} {trialDaysRemaining === 1 ? 'dia restante' : 'dias restantes'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Você está usando o FreelaDash em modo teste. Assine o Pro para continuar usando após o período de avaliação.
              </p>
              <div className="mt-3">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>{trialDaysUsed} de 7 dias usados</span>
                  <span>{Math.round((trialDaysUsed / 7) * 100)}%</span>
                </div>
                <Progress value={(trialDaysUsed / 7) * 100} className="h-2" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trial Expired Banner */}
      {isTrialExpired && !isPro && (
        <div className="rounded-lg border-2 border-destructive/50 bg-destructive/10 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-destructive/20">
              <Clock className="h-5 w-5 text-destructive" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-destructive">Período de Teste Expirado</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Seu período de teste terminou. Assine o plano Pro para continuar criando projetos, clientes e registrando tempo.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan Status */}
      <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/30">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-full ${isPro ? 'bg-primary/10' : 'bg-muted'}`}>
            <Crown className={`h-6 w-6 ${isPro ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-lg">
                {isPro ? 'Plano Pro' : isTrial ? 'Período de Teste' : 'Trial Expirado'}
              </h4>
              <Badge variant={isPro ? "default" : isTrial ? "outline" : "destructive"}>
                {isPro ? 'Ativo' : isTrial ? 'Em Avaliação' : 'Expirado'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isPro 
                ? 'Acesso completo a todos os recursos'
                : isTrial
                  ? 'Acesso total por 7 dias de uso'
                  : 'Assine o Pro para continuar usando'
              }
            </p>
            {subscription?.subscription_end && isPro && (
              <p className="text-xs text-muted-foreground mt-1">
                Próxima cobrança: {format(new Date(subscription.subscription_end), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshSubscription.isPending}
          >
            <RefreshCw className={`h-4 w-4 ${refreshSubscription.isPending ? 'animate-spin' : ''}`} />
          </Button>
          {isPro && (
            <Button 
              variant="outline"
              onClick={handleManageSubscription}
              disabled={customerPortal.isPending}
            >
              {customerPortal.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <ExternalLink className="h-4 w-4 mr-2" />
              )}
              Gerenciar
            </Button>
          )}
        </div>
      </div>

      {/* Upgrade Options for Free users */}
      {!isPro && (
        <div className="grid gap-4 md:grid-cols-2">
          {/* Monthly Plan */}
          <div className="rounded-xl border-2 border-primary bg-gradient-to-br from-primary/5 to-primary/10 p-5 relative overflow-hidden">
            <div className="absolute top-3 right-3">
              <Badge className="bg-primary">Recomendado</Badge>
            </div>
            <h4 className="font-semibold text-lg">Pro Mensal</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Todos os recursos para freelancers
            </p>
            <div className="mt-4">
              <span className="text-3xl font-bold">R$ 49,90</span>
              <span className="text-muted-foreground">/mês</span>
            </div>
            <ul className="mt-4 space-y-2">
              {PRO_FEATURES.slice(0, 4).map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            <Button 
              className="w-full mt-5"
              onClick={() => handleSubscribe('mensal')}
              disabled={loadingPlan === 'mensal'}
            >
              {loadingPlan === 'mensal' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                'Assinar Pro Mensal'
              )}
            </Button>
          </div>

          {/* Annual Plan */}
          <div className="rounded-xl border p-5 bg-card">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-lg">Pro Anual</h4>
              <Badge variant="secondary">33% OFF</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Economia de 2 meses
            </p>
            <div className="mt-4">
              <span className="text-3xl font-bold">R$ 399,90</span>
              <span className="text-muted-foreground">/ano</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Equivale a R$ 33,33/mês
            </p>
            <ul className="mt-4 space-y-2">
              {PRO_FEATURES.slice(0, 4).map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            <Button 
              className="w-full mt-5"
              variant="outline"
              onClick={() => handleSubscribe('anual')}
              disabled={loadingPlan === 'anual'}
            >
              {loadingPlan === 'anual' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                'Assinar Pro Anual'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Pro user features list */}
      {isPro && (
        <div className="rounded-lg border p-4 bg-muted/30">
          <h5 className="font-medium mb-3">Seus benefícios Pro:</h5>
          <div className="grid gap-2 sm:grid-cols-2">
            {PRO_FEATURES.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
                {feature}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Promo Code Input */}
      <div className="rounded-lg border p-4 bg-muted/10 mt-6">
        <h5 className="font-medium mb-3 text-sm">Possui um código promocional?</h5>
        <div className="flex items-center gap-2 max-w-sm">
          <Input 
            placeholder="Digite seu código..."
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
          />
          <Button 
            variant="outline" 
            onClick={handleRedeemPromo}
            disabled={redeeming || !promoCode.trim()}
          >
            {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Resgatar'}
          </Button>
        </div>
      </div>

    </div>
  );
}
